import { Analytics } from '@plaudit/analytics';
import CarouselSlide from './CarouselSlide';

export interface CarouselOptions {
	carouselEl: HTMLElement;
	allowIndicatorClick?: boolean;
	autoAdvance?: boolean;
	autoHeight?: boolean;
	analyticsCategory?: string;
	duration?: number;
	onChange?: (slideIndex: number) => void;
	onNext?: (slideIndex: number) => void;
	onPrev?: (slideIndex: number) => void;
	progressEl?: HTMLElement;
}

// interface CarouselState {
// 	priorIndex: number;
// 	currentIndex: number;
// }

export default abstract class CarouselAbstract {
	public carouselEl: HTMLElement;

	protected progressEl: HTMLElement;
	protected analytics: Analytics;
	protected controls: HTMLElement;
	protected controlNext: HTMLElement;
	protected controlPrev: HTMLElement;
	protected eventLabels = {
		previous: 'Previous',
		next: 'Next',
		indicator: 'Indicator',
	};
	protected timeout: number;
	protected slides: CarouselSlide[] = [];
	protected indicators: Element[] = [];


	// Options
	protected allowIndicatorClick: boolean;
	protected analyticsCategory: string;
	protected autoAdvance: boolean;
	protected autoHeight: boolean;
	protected duration: number;
	protected onChange = (slideIndex: number) => { };
	protected onNext = (slideIndex: number) => { };
	protected onPrev = (slideIndex: number) => { };
	protected startingIndex: number = 0;

	private previousIndex: number = undefined;
	private currentIndex: number = 0;

	constructor(options: CarouselOptions) {
		this.carouselEl = options.carouselEl;

		if (!this.carouselEl) { return };

		this.onChange = options.onChange || this.onChange;
		this.onNext = options.onNext || this.onNext;
		this.onPrev = options.onPrev || this.onPrev;

		this.duration = options.duration;
		this.allowIndicatorClick = options.allowIndicatorClick;
		this.autoAdvance = options.autoAdvance;
		this.autoHeight = options.autoHeight;

		this.analyticsCategory = options.analyticsCategory;
		this.analytics = this.analyticsCategory ? Analytics.getInstance() : undefined;

		this.progressEl = options.progressEl;
	}

	private setDirection(newIndex: number): void {
		let curIndex: number = this.currentIndex;
		let maxIndex: number = this.slides.length - 1;
		let direction: string = '';

		if (newIndex == 0 && curIndex == maxIndex) {
			direction = 'next';
		} else if (curIndex == 0 && newIndex == maxIndex) {
			direction = 'prev';
		} else {
			direction = (newIndex < curIndex) ? 'prev' : 'next';
		}
		this.carouselEl.setAttribute('data-direction', direction);
	}

	public activateIndexPrev(): void {
		const newIndex: number = this.getPrevIndex();
		this.activateIndex(newIndex);
		this.onPrev(newIndex);
	}
	public activateIndexNext(): void {
		const newIndex: number = this.getNextIndex();
		this.activateIndex(newIndex);
		this.onNext(newIndex);
	}
	public activateIndex(newIndex: number): void {
		if (this.progressEl){
			this.progressEl.setAttribute('data-slide-index', (newIndex + 1).toString());
		}
		if (this.currentIndex === newIndex) return;

		this.setDirection(newIndex);

		this.slides.forEach((slide: CarouselSlide, index: number) => {
			// slides
			if (index === newIndex) {
				slide.activate();
			} else if (slide.isActive) {
				slide.deactivate();
			} else if (slide.isOutgoing) {
				slide.clear();
			}
			// indicators
			this.indicators[index].setAttribute('aria-selected', index === newIndex ? 'true' : 'false');
		});

		this.updateIndex(newIndex);
		this.onChange(newIndex);

		if (this.autoAdvance) {
			this.initAutoAdvance();
		}
	}

	public getPrevIndex(): number {
		// Loops around if at start
		return this.currentIndex === 0 ? this.slides.length - 1 : this.currentIndex - 1;
	}
	public getNextIndex(): number {
		// Loops around if at end
		return this.currentIndex === this.slides.length - 1 ? 0 : this.currentIndex + 1;
	}
	public updateIndex(newIndex: number): void {
		this.previousIndex = this.currentIndex;
		this.currentIndex = newIndex;
	}

	protected buildControls(container: HTMLElement): void {
		if (this.slides.length <= 1) return;

		const element: Element = document.createElement('div');
		const controls = `
			<button class="control control--prev" title="Previous Slide"><span>Prev</span></button>
			<button class="control control--next" title="Next Slide"><span>Next</span></button>
		`;
		element.className = 'controls';
		element.innerHTML = controls;
		const nextElement: Element = element.querySelector('button.control--next');
		const prevElement: Element = element.querySelector('button.control--prev');

		prevElement.addEventListener('click', (event: MouseEvent) => {
			this.activateIndexPrev();
			this.trackControlEvent(this.eventLabels.previous);
		});
		nextElement.addEventListener('click', (event: MouseEvent) => {
			this.activateIndexNext();
			this.trackControlEvent(this.eventLabels.next);
		});

		container.appendChild(element);
	}
	protected buildIndicators() {
		if (this.slides.length <= 1) return;

		const indicators = this.carouselEl.querySelectorAll('[role="tab"]');

		for (let i = 0, l = this.slides.length; i < l; i++) {
			const element = indicators[i];
			this.indicators.push(element);
			if (indicators[i].getAttribute('aria-selected') === 'true') {
				this.startingIndex = i;
			}
			if (this.allowIndicatorClick) {
				indicators[i].addEventListener('click', (event: MouseEvent) => {
					this.trackControlEvent(this.eventLabels.indicator);
					this.activateIndex(i);
				});
			} else {
				indicators[i].classList.add('disabled');
				indicators[i].addEventListener('click', (event: MouseEvent) => {
					event.preventDefault();
				});
			}
		}
	}
	protected buildSlideSentinel() {
		if (!this.autoHeight) return;

		function getMaxOfArray(numArray: Number[]) {
			return Math.max.apply(null, numArray);
		}
		const slidesHeight: Number[] = [];
		// const slidesParent = this.carouselEl.querySelector('[role="tabpanel"]').parentNode;
		const slides = this.carouselEl.querySelectorAll('[role="tabpanel"]');
		for (let i = 0, l = slides.length; i < l; i++) {
			slidesHeight.push((slides[i].querySelector('.slide__contentInner') as HTMLElement).offsetHeight);
		}
		const maxValue = getMaxOfArray(slidesHeight);
		const maxIndex = slidesHeight.indexOf(maxValue);
		// console.log(maxIndex, maxValue);

		let sentinel = slides[maxIndex].cloneNode(true) as HTMLElement;
		sentinel.removeAttribute('id');
		sentinel.removeAttribute('aria-labelledby');
		sentinel.removeAttribute('role');
		sentinel.setAttribute('data-status', 'sentinel');
		sentinel.setAttribute('hidden', '');

		(slides[0]).parentNode.insertBefore(sentinel, slides[0]);
	}
	protected buildSlides() {
		const slides = this.carouselEl.querySelectorAll('[role="tabpanel"]');
		for (let i = 0, l = slides.length; i < l; i++) {
			const element = slides[i] as HTMLElement;
			const isActive = i === this.startingIndex;
			this.slides.push(new CarouselSlide({ element, isActive }));
		}

		this.buildSlideSentinel();
	}
	protected initAutoAdvance(): void {
		if (this.autoAdvance && this.duration) {
			if (this.timeout) {
				clearTimeout(this.timeout);
			}

			this.timeout = window.setTimeout(() => {
				this.activateIndexNext();
			}, this.duration);

			this.setDirection(1);
		}
	}

	protected trackControlEvent = (label: string) => {
		return this.analytics
			? this.analytics.trackEvent(this.analyticsCategory, 'Control Click', label)
			: null;
	}
}
